import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import dryEditorClass from './DryEditorClass';
import constants from '../../constants';
import FastTooltip from '../../components/FastTooltip';

const { EDITOR_COMMAND_NAME_MAP } = constants;

const FONT_FAMILIES = [
  { label: 'Default', value: 'inherit' },
  // Web safe
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Trebuchet MS', value: '"Trebuchet MS", Helvetica, sans-serif' },
  // Google Fonts
  { label: 'Inter', value: 'Inter, sans-serif' },
  { label: 'Roboto', value: 'Roboto, sans-serif' },
  { label: 'Lato', value: 'Lato, sans-serif' },
  { label: 'Playfair Display', value: '"Playfair Display", serif' },
  { label: 'Merriweather', value: 'Merriweather, serif' },
  { label: 'Fira Code', value: '"Fira Code", monospace' },
];

const getFontFamilyFromState = (editorState) => {
  if (!editorState) return 'inherit';
  try {
    const { $from, empty, from, to } = editorState.selection;
    const fontFamilyMark = editorState.schema.marks.fontfamily;
    if (!fontFamilyMark) return 'inherit';
    if (empty) {
      const mark = $from.marks().find(m => m.type === fontFamilyMark);
      return mark ? mark.attrs.fontFamily : 'inherit';
    }
    let found = null;
    editorState.doc.nodesBetween(from, to, (node) => {
      if (node.isText && !found) {
        const mark = node.marks.find(m => m.type === fontFamilyMark);
        if (mark) found = mark.attrs.fontFamily;
      }
    });
    return found || 'inherit';
  } catch (_) {
    return 'inherit';
  }
};

const FontFamilySelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentFont, setCurrentFont] = useState('inherit');
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (btnRef.current && !btnRef.current.closest('.font-family-dropdown').contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const update = (state) => setCurrentFont(getFontFamilyFromState(state));
    dryEditorClass.registerTransactionHandler({ name: 'fontFamilySelector', method: update });
    return () => dryEditorClass.registeredTransactionHandlers.delete('fontFamilySelector');
  }, []);

  const handleToggle = () => {
    if (!isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left });
    }
    setIsOpen((prev) => !prev);
  };

  const applyFont = (fontFamily, e) => {
    e.preventDefault();
    dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.font_family, options: { fontFamily } }]);
    setIsOpen(false);
  };

  const currentLabel = FONT_FAMILIES.find(f => f.value === currentFont)?.label || 'Font';

  const menu = isOpen ? ReactDOM.createPortal(
    <div
      className="font-family-dropdown__menu"
      role="listbox"
      style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
    >
      {FONT_FAMILIES.map((font) => (
        <button
          key={font.value}
          className="font-family-dropdown__option"
          role="option"
          aria-selected={currentFont === font.value}
          onMouseDown={(e) => applyFont(font.value, e)}
          type="button"
          style={{ fontFamily: font.value !== 'inherit' ? font.value : undefined }}
        >
          {font.label}
        </button>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <FastTooltip title="Font family">
      <div className="font-family-dropdown">
        <button
          ref={btnRef}
          className="font-family-dropdown__btn"
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          type="button"
        >
          <span className="font-family-dropdown__label">{currentLabel}</span>
          <span className="material-icons font-family-dropdown__caret">
            {isOpen ? 'arrow_drop_up' : 'arrow_drop_down'}
          </span>
        </button>
        {menu}
      </div>
    </FastTooltip>
  );
};

export default FontFamilySelector;
